import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { createGroupPost, getGroupPosts } from "../../functions/post";
import Post from "../post";
import CreateGroupPostPopup from "../createGroupPostPopup";
import FeelingActivitySelector from "../createPostPopup/FeelingActivitySelector";
import "./style.css";

const TABS = [
  { id: "posts", label: "Posts" },
  { id: "members", label: "Members" },
  { id: "media", label: "Media" },
  { id: "files", label: "Files" },
  { id: "about", label: "About" },
];

const DEFAULT_GROUP_PICTURE = "/images/default_group.svg";

function getMemberDisplayName(member) {
  if (!member) return "Member";
  const full = [member.first_name, member.last_name].filter(Boolean).join(" ").trim();
  return full || member.name || member.username || "Member";
}

function formatMemberRole(member, group) {
  const memberId = String(member?._id || member?.id || "");
  const ownerId = String(group?.owner?._id || group?.owner?.id || group?.owner || "");
  if (memberId && ownerId && memberId === ownerId) return "Owner";
  if (member?.role === "admin" || member?.isAdmin) return "Admin";
  const admins = group?.admins || [];
  const isAdmin = admins.some(
    (admin) => String(admin?._id || admin?.id || admin) === memberId
  );
  return isAdmin ? "Admin" : "Member";
}

export default function GroupPosts({ group, onBack, onInvite }) {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showFeelingPicker, setShowFeelingPicker] = useState(false);
  const [selectedFeeling, setSelectedFeeling] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [postingFeeling, setPostingFeeling] = useState(false);
  const [activeTab, setActiveTab] = useState("posts");
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(null);

  const isPrivate = !!group?.isPrivate;
  const groupPicture = group?.groupPicture || DEFAULT_GROUP_PICTURE;
  const shortDescription = (group?.description || "").trim();
  const members = useMemo(() => {
    const membersMap = new Map();
    const upsert = (value) => {
      if (!value) return;
      const id = String(value._id || value.id || (typeof value === "string" ? value : ""));
      if (!id) return;
      const memberObject = typeof value === "object" ? value : { _id: id };
      membersMap.set(id, { ...(membersMap.get(id) || {}), ...memberObject });
    };

    (group?.members || []).forEach(upsert);
    (group?.admins || []).forEach(upsert);
    upsert(group?.owner);

    return Array.from(membersMap.values());
  }, [group]);
  const memberCount = members.length;

  const mediaItems = useMemo(() => {
    const items = [];
    posts.forEach((post) => {
      (post.images || []).forEach((image, index) => {
        const url = typeof image === "string" ? image : image?.url;
        if (!url) return;
        const type =
          typeof image === "object" && image?.type
            ? image.type
            : /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url)
              ? "video"
              : "image";
        items.push({
          id: `${post._id}-${index}`,
          url,
          type,
          postId: post._id,
        });
      });
    });
    return items;
  }, [posts]);

  const selectedMedia =
    selectedMediaIndex !== null ? mediaItems[selectedMediaIndex] : null;

  const loadGroupPosts = useCallback(
    async (isMounted = () => true) => {
      try {
        if (!isMounted()) return;
        setLoading(true);
        setError("");
        const response = await getGroupPosts(group.id, user.token);

        if (!isMounted()) return;

        if (response.status === "ok") {
          setPosts(response.data);
        } else {
          setError(response.message);
        }
      } catch (err) {
        if (isMounted()) {
          setError("Failed to load group posts");
        }
      } finally {
        if (isMounted()) {
          setLoading(false);
        }
      }
    },
    [group?.id, user.token]
  );

  useEffect(() => {
    let mounted = true;

    const loadPostsSafe = async () => {
      if (group?.id && mounted) {
        await loadGroupPosts(() => mounted);
      }
    };

    loadPostsSafe();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id, loadGroupPosts]);

  useEffect(() => {
    if (selectedMediaIndex === null) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedMediaIndex(null);
      } else if (event.key === "ArrowLeft" && mediaItems.length > 1) {
        setSelectedMediaIndex((prev) =>
          prev > 0 ? prev - 1 : mediaItems.length - 1
        );
      } else if (event.key === "ArrowRight" && mediaItems.length > 1) {
        setSelectedMediaIndex((prev) =>
          prev < mediaItems.length - 1 ? prev + 1 : 0
        );
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedMediaIndex, mediaItems.length]);

  const handlePostCreated = (newPost) => {
    setPosts([newPost, ...posts]);
    setActiveTab("posts");
  };

  const toTitle = (id) =>
    String(id || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const handleQuickFeelingPost = async () => {
    if (!selectedFeeling && !selectedActivity) return;
    try {
      setPostingFeeling(true);
      const parts = [];
      if (selectedFeeling) parts.push(`is feeling ${toTitle(selectedFeeling)}`);
      if (selectedActivity) parts.push(`is ${toTitle(selectedActivity)}`);
      const text = parts.join(" and ");
      const res = await createGroupPost(
        null,
        null,
        text || " ",
        null,
        user.id,
        group.id,
        user.token,
        selectedFeeling,
        selectedActivity,
        null
      );
      setPostingFeeling(false);
      if (res.status === "ok") {
        setPosts((prev) => [res.data, ...prev]);
        setSelectedFeeling(null);
        setSelectedActivity(null);
        setShowFeelingPicker(false);
        setActiveTab("posts");
      } else {
        setShowFeelingPicker(false);
        setShowCreatePost(true);
        setError(res.message || "Failed to post feeling");
      }
    } catch (err) {
      setPostingFeeling(false);
      setShowFeelingPicker(false);
      setShowCreatePost(true);
    }
  };

  const handleInvite = () => {
    if (typeof onInvite === "function") {
      onInvite(group);
      return;
    }
    setActiveTab("members");
  };

  if (!group) {
    return <div className="group-posts-error">Group not found</div>;
  }

  const createdLabel = group.createdAt
    ? new Date(group.createdAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="group-posts">
      <div className="group-posts-header">
        <button type="button" className="back-btn" onClick={onBack} aria-label="Back to Groups">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <path d="M19 12H5m7-7l-7 7 7 7" />
          </svg>
          <span>Groups</span>
        </button>

        <div className="group-cover">
          <div
            className="group-cover-bg"
            style={
              group.groupPicture
                ? { backgroundImage: `url(${group.groupPicture})` }
                : undefined
            }
          />
          <div className="group-cover-overlay" />
        </div>

        <div className="group-identity">
          <div className="group-header-picture">
            <img
              src={groupPicture}
              alt={`${group.name} group`}
              className="group-header-img"
            />
          </div>

          <div className="group-identity-main">
            <div className="group-identity-text">
              <h2>{group.name}</h2>
              <div className="group-meta-row">
                <span className={`group-privacy ${isPrivate ? "private" : "public"}`}>
                  {isPrivate ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0110 0v4" />
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" />
                    </svg>
                  )}
                  {isPrivate ? "Private group" : "Public group"}
                </span>
                <span className="group-meta-dot" aria-hidden="true">
                  ·
                </span>
                <span className="group-member-count">
                  {memberCount} {memberCount === 1 ? "member" : "members"}
                </span>
              </div>
              {shortDescription && (
                <p className="group-description">{shortDescription}</p>
              )}
            </div>
          </div>
        </div>

        <nav className="group-tabs" aria-label="Group sections">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`group-tab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {tab.id === "members" && memberCount > 0 && (
                <span className="group-tab-count">{memberCount}</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "posts" && (
        <>
          <div className="create-group-post">
            <div className="create-post-header">
              <img src={user.picture} alt="" className="create-post-avatar" />
              <button
                type="button"
                className="create-post-input"
                onClick={() => setShowCreatePost(true)}
              >
                What&apos;s on your mind, {user.first_name}?
              </button>
            </div>
            <div className="create-post-actions">
              <button
                type="button"
                className="create-post-btn"
                onClick={() => setShowCreatePost(true)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
                Photo/Video
              </button>
              <button
                type="button"
                className="create-post-btn"
                onClick={() => setShowFeelingPicker(true)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 19l7-7 3 3-7 7-3-3z" />
                  <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
                  <path d="M2 2l7.586 7.586" />
                  <circle cx="11" cy="11" r="2" />
                </svg>
                Feeling/Activity
              </button>
            </div>
          </div>

          {error && <div className="group-posts-error">{error}</div>}

          {loading ? (
            <div className="group-posts-loading">
              <div className="loading-spinner" />
              <p>Loading posts...</p>
            </div>
          ) : (
            <div className="group-posts-list">
              {posts.length > 0 ? (
                posts.map((post) => (
                  <Post
                    key={post._id}
                    post={post}
                    user={user}
                    isGroupPost={true}
                    groupName={group.name}
                  />
                ))
              ) : (
                <div className="no-posts">
                  <div className="no-posts-icon">
                    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                      <path d="M8 9h8M8 13h5" />
                    </svg>
                  </div>
                  <h3>Start the conversation</h3>
                  <p>
                    {group.name} is waiting for its first post. Share an update, photo, or idea
                    to get things going.
                  </p>
                  <button
                    type="button"
                    className="blue_btn"
                    onClick={() => setShowCreatePost(true)}
                  >
                    Create first post
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === "members" && (
        <div className="group-panel">
          <div className="group-panel-header">
            <h3>Members ({memberCount})</h3>
            <button type="button" className="group-action-btn primary compact" onClick={handleInvite}>
              Invite
            </button>
          </div>
          {members.length > 0 ? (
            <ul className="group-members-list">
              {members.map((member) => {
                const key = member._id || member.id || member.username;
                const role = formatMemberRole(member, group);
                return (
                  <li key={key} className="group-member-item">
                    <img
                      src={member.picture || DEFAULT_GROUP_PICTURE}
                      alt=""
                      className="group-member-avatar"
                    />
                    <div className="group-member-info">
                      <span className="group-member-name">{getMemberDisplayName(member)}</span>
                      <span className="group-member-role">{role}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="group-empty-panel">
              <h3>No members yet</h3>
              <p>Invite friends to grow this group.</p>
              <button type="button" className="blue_btn" onClick={handleInvite}>
                Invite people
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === "media" && (
        <div className="group-panel">
          {loading ? (
            <div className="group-posts-loading">
              <div className="loading-spinner" />
              <p>Loading media...</p>
            </div>
          ) : mediaItems.length > 0 ? (
            <div className="group-media-grid">
              {mediaItems.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  className="group-media-item"
                  onClick={() => setSelectedMediaIndex(index)}
                  aria-label={item.type === "video" ? "Open video" : "Open photo"}
                >
                  {item.type === "video" ? (
                    <>
                      <video
                        src={item.url}
                        muted
                        playsInline
                        preload="metadata"
                      />
                      <span className="group-media-video-badge" aria-hidden="true">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    </>
                  ) : (
                    <img src={item.url} alt="" />
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="group-empty-panel">
              <div className="no-posts-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
              </div>
              <h3>No media yet</h3>
              <p>Photos and videos shared in {group.name} will show up here.</p>
              <button type="button" className="blue_btn" onClick={() => setShowCreatePost(true)}>
                Share a photo
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === "files" && (
        <div className="group-panel">
          <div className="group-empty-panel">
            <div className="no-posts-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <path d="M14 2v6h6M8 13h8M8 17h6" />
              </svg>
            </div>
            <h3>No files shared</h3>
            <p>Documents and files shared with the group will appear in this space.</p>
          </div>
        </div>
      )}

      {activeTab === "about" && (
        <div className="group-panel">
          <div className="group-about-card">
            <h3>About this group</h3>
            <p className="group-about-description">
              {shortDescription || "No description has been added for this group yet."}
            </p>
            <dl className="group-about-meta">
              <div>
                <dt>Privacy</dt>
                <dd>{isPrivate ? "Private" : "Public"}</dd>
              </div>
              <div>
                <dt>Members</dt>
                <dd>{memberCount}</dd>
              </div>
              {createdLabel && (
                <div>
                  <dt>Created</dt>
                  <dd>{createdLabel}</dd>
                </div>
              )}
              {group.userRole && (
                <div>
                  <dt>Your role</dt>
                  <dd className="capitalize">{group.userRole}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      )}

      {showCreatePost && (
        <CreateGroupPostPopup
          user={user}
          setVisible={setShowCreatePost}
          groupId={group.id}
          groupName={group.name}
          onPostCreated={handlePostCreated}
        />
      )}

      {showFeelingPicker && (
        <FeelingActivitySelector
          selectedFeeling={selectedFeeling}
          selectedActivity={selectedActivity}
          onFeelingSelect={setSelectedFeeling}
          onActivitySelect={setSelectedActivity}
          onClose={() => {
            setShowFeelingPicker(false);
            setSelectedFeeling(null);
            setSelectedActivity(null);
          }}
          onSubmit={handleQuickFeelingPost}
          submitLabel={postingFeeling ? "Sending..." : "Send"}
        />
      )}

      {selectedMedia && (
        <div
          className="group-media-lightbox"
          onClick={() => setSelectedMediaIndex(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Media viewer"
        >
          <button
            type="button"
            className="group-media-lightbox-close"
            onClick={() => setSelectedMediaIndex(null)}
            aria-label="Close"
          >
            ×
          </button>

          <div
            className="group-media-lightbox-content"
            onClick={(event) => event.stopPropagation()}
          >
            {selectedMedia.type === "video" ? (
              <video
                key={selectedMedia.id}
                src={selectedMedia.url}
                controls
                autoPlay
                playsInline
                className="group-media-lightbox-media"
              />
            ) : (
              <img
                src={selectedMedia.url}
                alt=""
                className="group-media-lightbox-media"
              />
            )}
          </div>

          {mediaItems.length > 1 && (
            <>
              <button
                type="button"
                className="group-media-lightbox-nav prev"
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedMediaIndex((prev) =>
                    prev > 0 ? prev - 1 : mediaItems.length - 1
                  );
                }}
                aria-label="Previous media"
              >
                ‹
              </button>
              <button
                type="button"
                className="group-media-lightbox-nav next"
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedMediaIndex((prev) =>
                    prev < mediaItems.length - 1 ? prev + 1 : 0
                  );
                }}
                aria-label="Next media"
              >
                ›
              </button>
              <div className="group-media-lightbox-counter">
                {selectedMediaIndex + 1} / {mediaItems.length}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
